// src/components/WhatsAppPro.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';

// ==================================================================================
// IMPORTAÇÃO DA API CENTRALIZADA
// ==================================================================================
import api from '../../services/api';

// ============================================================================
// 🎨 THEME & ICONS (WhatsApp / Omnichannel PRO)
// ============================================================================
const THEME = {
  primary: '#00a884',      
  primaryDark: '#008069',  
  bgApp: '#eff2f5',        
  bgChat: '#efeae2',       
  bgBubbleMe: '#d9fdd3',   
  bgBubbleOther: '#ffffff',
  textMain: '#111b21',
  textMuted: '#667781',
  border: '#d1d7db',
  glass: 'rgba(255, 255, 255, 0.95)',
};

const Icons = {
  Search: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#54656f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
  Send: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>,
  Bot: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00a884" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"></rect><circle cx="12" cy="5" r="2"></circle><path d="M12 7v4"></path></svg>,
  Phone: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>,
  CheckAll: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#53bdeb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
  Note: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
  Close: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
};

// ============================================================================
// ⚙️ HELPERS PARA AGRUPAR POR DATA & IDENTIFICAR IMAGENS
// ============================================================================
const groupMessagesByDate = (messages) => {
    if (!messages || messages.length === 0) return {};
    const groups = {};
    
    messages.forEach(msg => {
      const d = new Date(msg.rawDate);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let dateKey = d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

      if (d.toDateString() === today.toDateString()) dateKey = 'Hoje';
      else if (d.toDateString() === yesterday.toDateString()) dateKey = 'Ontem';
      
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(msg);
    });
    return groups;
};

// Verifica se o texto é uma string Base64 de imagem
const isBase64Image = (text) => {
    if (!text || typeof text !== 'string') return false;
    if (text.length > 200 && !text.includes(' ')) {
        return text.startsWith('/9j/') || text.startsWith('iVBORw0KGgo') || text.startsWith('data:image');
    }
    return false;
};

// Formata o src da imagem corretamente
const formatImageSrc = (text) => {
    if (text.startsWith('data:image')) return text;
    if (text.startsWith('/9j/')) return `data:image/jpeg;base64,${text}`;
    if (text.startsWith('iVBORw0KGgo')) return `data:image/png;base64,${text}`;
    return `data:image/jpeg;base64,${text}`; 
};

// ============================================================================
// 🛠️ COMPONENT
// ============================================================================
export default function WhatsAppPro() {
  const [clientes, setClientes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [historico, setHistorico] = useState({});
  const [typedMessage, setTypedMessage] = useState('');
  const [search, setSearch] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null); 

  const socket = useRef(null);
  const chatEndRef = useRef(null);
  
  // URL base para Socket.IO (pega do Axios default ou fallback)
  const socketUrl = api.defaults.baseURL || window.location.origin;

  const scrollToBottom = () => setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

  // 1. INIT SOCKET & STATUS
  useEffect(() => {
    socket.current = io(socketUrl, { transports: ['websocket'] });
    
    socket.current.on('whatsapp_status', (d) => setIsConnected(d.connected));
    
    socket.current.on('incoming_message', (data) => {
      const targetId = data.context?.osId;
      if (!targetId) return;

      setHistorico(prev => {
        const chat = prev[targetId] || [];
        // Evita duplicatas se a mensagem chegou muito rápido
        if (chat.some(m => m.text === data.body && Math.abs(new Date(m.rawDate) - new Date(data.rawDate)) < 2000)) return prev;
        
        return { ...prev, [targetId]: [...chat, { text: data.body, sender: 'client', time: new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}), rawDate: data.rawDate, type: 'chat' }] };
      });
      scrollToBottom();
    });

    api.get('/api/whatsapp/status').then(r => setIsConnected(r.data.connected)).catch(() => {});

    return () => socket.current?.disconnect();
  }, [socketUrl]);

  // 2. BUSCA CLIENTES
  useEffect(() => {
    api.get('/ordens-servico')
      .then(r => {
        const validos = r.data.filter(os => os.whatsapp).map(os => ({
          ...os, 
          id: os._id, 
          whatsappClean: os.whatsapp.replace(/\D/g, ''),
        }));
        setClientes(validos);
      });
  }, []);

  // 3. MERGE HISTÓRICO
  useEffect(() => {
    if (!selectedId) return;
    
    api.get(`/api/whatsapp/history/${selectedId}`)
      .then(r => {
        const client = clientes.find(c => c.id === selectedId);
        
        // Logs do MongoDB
        const timelineLogs = (client?.registros || []).map(r => {
            const txt = r.texto.toLowerCase();
            let senderType = 'system';
            let msgType = 'event';

            if (txt.includes('robô') || txt.includes('pós-venda') || txt.includes('payload') || txt.includes('enviada') || txt.includes('automática')) {
                senderType = 'bot';
                msgType = 'chat';
            } 
            else if (r.usuario || txt.includes('anotação') || txt.includes('observação')) {
                senderType = 'note';
                msgType = 'chat'; 
            }

            return { 
                text: r.texto, 
                sender: senderType, 
                time: new Date(r.createdAt).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}), 
                rawDate: r.createdAt, 
                type: msgType 
            };
        });

        // Chat do Baileys
        const apiChat = r.data.map(m => ({ 
            text: m.text, 
            sender: m.sender, 
            time: m.time, 
            rawDate: m.rawDate, 
            type: 'chat' 
        }));

        const unifiedHistory = [...apiChat, ...timelineLogs].sort((a,b) => new Date(a.rawDate) - new Date(b.rawDate));

        setHistorico(prev => ({ ...prev, [selectedId]: unifiedHistory }));
        scrollToBottom();
      }).catch(err => console.log(err));
  }, [selectedId, clientes]);

  // 4. ENVIAR MENSAGEM
  const handleSend = async () => {
    if (!typedMessage.trim() || !selectedId) return;
    
    const client = clientes.find(c => c.id === selectedId);
    const msgObj = { text: typedMessage, sender: 'me', time: new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}), rawDate: new Date().toISOString(), type: 'chat' };
    
    setHistorico(prev => ({ ...prev, [selectedId]: [...(prev[selectedId] || []), msgObj] }));
    const textToSend = typedMessage;
    setTypedMessage('');
    scrollToBottom();

    try {
      await api.post('/api/whatsapp/send', { number: client.whatsappClean, message: textToSend, osId: selectedId });
    } catch {
      alert("Falha no envio da mensagem.");
    }
  };

  const filteredClients = useMemo(() => {
    return clientes.filter(c => c.cliente?.nome?.toLowerCase().includes(search.toLowerCase()) || String(c.numeroOS).includes(search));
  }, [clientes, search]);

  const selectedClient = clientes.find(c => c.id === selectedId);
  const currentChat = historico[selectedId] || [];
  const groupedChat = groupMessagesByDate(currentChat);

  return (
    <div className="wapro-container">
      
      {/* SIDEBAR */}
      <aside className="wapro-sidebar">
        <div className="ws-header">
            <div className="ws-user-avatar">MZ</div>
            <div className="ws-header-actions">
               <span title="Status" className={`status-dot ${isConnected ? 'online' : 'offline'}`}></span>
            </div>
        </div>
        
        <div className="ws-search-wrapper">
            <div className="ws-search-bar">
                <Icons.Search />
                <input 
                    type="text" 
                    placeholder="Pesquisar ou começar uma nova conversa" 
                    value={search} 
                    onChange={(e) => setSearch(e.target.value)} 
                />
            </div>
        </div>

        <div className="ws-contact-list">
            {filteredClients.map(c => {
                const isSelected = selectedId === c.id;
                const hist = historico[c.id] || [];
                const lastMsg = hist.length > 0 ? hist[hist.length - 1] : null;

                let previewText = `OS #${c.numeroOS}`;
                if (lastMsg) {
                    if (isBase64Image(lastMsg.text)) {
                        previewText = lastMsg.sender === 'me' ? '📷 Foto' : '📷 Foto';
                    } else {
                        previewText = lastMsg.sender === 'me' ? `Você: ${lastMsg.text}` : lastMsg.text;
                    }
                }

                return (
                <div key={c.id} className={`contact-item ${isSelected ? 'active' : ''}`} onClick={() => setSelectedId(c.id)}>
                    <div className="ci-avatar">{c.cliente?.nome?.[0] || 'C'}</div>
                    <div className="ci-info">
                        <div className="ci-top">
                            <span className="ci-name">{c.cliente?.nome}</span>
                            <span className="ci-time">{lastMsg ? lastMsg.time : ''}</span>
                        </div>
                        <div className="ci-bottom">
                            {lastMsg?.sender === 'me' && <span className="read-receipt"><Icons.CheckAll /></span>}
                            <span className="ci-preview">{previewText}</span>
                        </div>
                    </div>
                </div>
                );
            })}
            {filteredClients.length === 0 && <div className="ws-empty">Nenhuma conversa encontrada.</div>}
        </div>
      </aside>

      {/* ÁREA DO CHAT */}
      <main className="wapro-main">
        {selectedClient ? (
            <>
                <header className="wm-header">
                    <div className="wm-header-info">
                        <div className="ci-avatar">{selectedClient.cliente?.nome?.[0]}</div>
                        <div className="wm-text-group">
                            <h3 className="wm-name">{selectedClient.cliente?.nome}</h3>
                            <span className="wm-status">
                                {isConnected ? 'online' : 'offline'} • OS #{selectedClient.numeroOS}
                            </span>
                        </div>
                    </div>
                </header>

                <div className="wm-feed chat-bg-pattern">
                    {Object.keys(groupedChat).map((date) => (
                        <div key={date}>
                            <div className="date-badge-wrapper">
                                <span className="date-badge">{date}</span>
                            </div>

                            {groupedChat[date].map((msg, idx) => {
                                const isMe = msg.sender === 'me';
                                const isBot = msg.sender === 'bot';
                                const isNote = msg.sender === 'note';
                                const isSys = msg.type === 'event' || msg.sender === 'system';

                                if (isSys) return (
                                    <div key={idx} className="sys-message-wrapper">
                                        <div className="sys-message">⚙️ {msg.text}</div>
                                    </div>
                                );

                                if (isNote) return (
                                    <div key={idx} className="sys-message-wrapper">
                                        <div className="note-message">
                                            <strong><Icons.Note /> Nota Interna:</strong> {msg.text}
                                        </div>
                                    </div>
                                );

                                const isImage = isBase64Image(msg.text);

                                return (
                                    <div key={idx} className={`bubble-row ${isMe ? 'row-me' : 'row-other'}`}>
                                        <div className={`bubble ${isMe ? 'bubble-me' : isBot ? 'bubble-bot' : 'bubble-other'} ${isImage ? 'bubble-image' : ''}`}>
                                            
                                            {isBot && <div className="bubble-author"><Icons.Bot /> Robô Vizum</div>}
                                            
                                            {isImage ? (
                                                <div className="image-wrapper" onClick={() => setExpandedImage(formatImageSrc(msg.text))}>
                                                    <img src={formatImageSrc(msg.text)} alt="Mídia" />
                                                </div>
                                            ) : (
                                                <div className="bubble-text">{msg.text}</div>
                                            )}
                                            
                                            <div className="bubble-meta">
                                                <span className="bubble-time">{msg.time}</span>
                                                {isMe && <span className="bubble-check"><Icons.CheckAll /></span>}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                    <div ref={chatEndRef} />
                </div>

                <footer className="wm-footer">
                    <div className="input-wrapper">
                        <textarea 
                            className="chat-textarea"
                            placeholder="Digite uma mensagem"
                            value={typedMessage}
                            onChange={(e) => setTypedMessage(e.target.value)}
                            onKeyDown={(e) => {
                                if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                            }}
                            disabled={!isConnected}
                        />
                        <button 
                            className={`btn-send ${typedMessage.trim() ? 'active' : ''}`} 
                            onClick={handleSend}
                            disabled={!typedMessage.trim() || !isConnected}
                        >
                            <Icons.Send />
                        </button>
                    </div>
                </footer>
            </>
        ) : (
            <div className="wm-empty-state chat-bg-pattern">
                <div className="empty-content">
                    <div className="empty-img">
                       <svg viewBox="0 0 24 24" width="120" height="120" fill="none" stroke="#e9edef" strokeWidth="1"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 1 1-7.6-13.5h.8c4.3 0 7.9 3.5 7.7 7.7z"></path></svg>
                    </div>
                    <h2>WhatsApp Web Pro</h2>
                    <p>Envie e receba mensagens sem precisar manter seu celular conectado.<br/>Use o WhatsApp em até 100 aparelhos ao mesmo tempo.</p>
                    <div className="encryption-notice">🔒 Protegido com criptografia de ponta a ponta</div>
                </div>
            </div>
        )}
      </main>

      {/* LIGHTBOX */}
      {expandedImage && (
        <div className="lightbox-overlay" onClick={() => setExpandedImage(null)}>
            <button className="lightbox-close" onClick={() => setExpandedImage(null)}><Icons.Close /></button>
            <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                <img src={expandedImage} alt="Zoom" />
            </div>
        </div>
      )}

      {/* STYLES */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Segoe+UI:wght@400;600&display=swap');

        .wapro-container {
            display: flex; height: calc(100vh - 140px); min-height: 600px;
            background: #fff; overflow: hidden; border-radius: 0;
            font-family: 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif;
            border: 1px solid ${THEME.border};
            box-shadow: 0 2px 5px rgba(0,0,0,0.05);
        }

        /* SIDEBAR */
        .wapro-sidebar { width: 400px; background: #fff; display: flex; flex-direction: column; border-right: 1px solid ${THEME.border}; }
        .ws-header { height: 60px; background: ${THEME.bgApp}; padding: 10px 16px; display: flex; justify-content: space-between; align-items: center; border-right: 1px solid ${THEME.border}; }
        .ws-user-avatar { width: 40px; height: 40px; border-radius: 50%; background: #dfe5e7; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 600; font-size: 14px; color: #54656f; }
        .status-dot { width: 10px; height: 10px; border-radius: 50%; background: #ccc; }
        .status-dot.online { background: #00a884; box-shadow: 0 0 0 2px #fff; }

        .ws-search-wrapper { padding: 8px 12px; border-bottom: 1px solid ${THEME.border}; background: #fff; }
        .ws-search-bar { background: ${THEME.bgApp}; border-radius: 8px; display: flex; align-items: center; padding: 0 12px; height: 35px; }
        .ws-search-bar input { flex: 1; border: none; background: transparent; padding: 0 12px; font-size: 14px; outline: none; color: ${THEME.textMain}; width: 100%; }

        .ws-contact-list { flex: 1; overflow-y: auto; background: #fff; }
        .contact-item { display: flex; gap: 15px; padding: 0 12px; height: 72px; align-items: center; cursor: pointer; transition: 0.2s; position: relative; }
        .contact-item:hover { background: #f5f6f6; }
        .contact-item.active { background: #f0f2f5; }
        .contact-item::after { content: ''; position: absolute; bottom: 0; right: 0; width: 82%; height: 1px; background: ${THEME.border}; }
        
        .ci-avatar { width: 49px; height: 49px; border-radius: 50%; background: #dfe5e7; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: 600; flex-shrink: 0; overflow: hidden; }
        .ci-info { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; gap: 2px; }
        .ci-top { display: flex; justify-content: space-between; align-items: center; }
        .ci-name { font-size: 16px; color: #111b21; font-weight: 400; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ci-time { font-size: 12px; color: #667781; }
        .ci-bottom { display: flex; align-items: center; gap: 4px; }
        .ci-preview { font-size: 14px; color: #667781; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .read-receipt svg { width: 16px; color: #53bdeb; }

        .ws-empty { text-align: center; padding: 40px 20px; color: ${THEME.textMuted}; font-size: 14px; }

        /* MAIN */
        .wapro-main { flex: 1; display: flex; flex-direction: column; background: #efeae2; position: relative; }
        .wm-header { height: 60px; background: ${THEME.bgApp}; display: flex; align-items: center; padding: 0 16px; border-bottom: 1px solid ${THEME.border}; z-index: 10; }
        .wm-header-info { display: flex; alignItems: center; gap: 15px; cursor: pointer; }
        .wm-text-group { display: flex; flex-direction: column; justify-content: center; }
        .wm-name { margin: 0; font-size: 16px; color: #111b21; font-weight: 400; }
        .wm-status { font-size: 13px; color: #667781; }

        .wm-feed { flex: 1; overflow-y: auto; padding: 20px 60px; display: flex; flex-direction: column; }
        .chat-bg-pattern { background-image: url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png"); background-repeat: repeat; opacity: 0.95; }

        .date-badge-wrapper { display: flex; justify-content: center; margin: 12px 0; }
        .date-badge { background: #fff; padding: 5px 12px; border-radius: 7.5px; font-size: 12.5px; color: #54656f; box-shadow: 0 1px 0.5px rgba(11,20,26,.13); text-transform: uppercase; }

        .sys-message-wrapper { display: flex; justify-content: center; margin-bottom: 12px; }
        .sys-message { background: #fff; padding: 5px 12px; border-radius: 7.5px; font-size: 12.5px; color: #54656f; box-shadow: 0 1px 0.5px rgba(11,20,26,.13); max-width: 80%; text-align: center; }
        .note-message { background: #fff5c4; padding: 8px 12px; border-radius: 7.5px; font-size: 13px; color: #54656f; box-shadow: 0 1px 0.5px rgba(11,20,26,.13); max-width: 70%; border: 1px solid #ffeeba; }

        /* BUBBLES */
        .bubble-row { display: flex; margin-bottom: 8px; width: 100%; }
        .row-me { justify-content: flex-end; }
        .row-other { justify-content: flex-start; }

        .bubble { max-width: 65%; padding: 6px 7px 8px 9px; border-radius: 7.5px; font-size: 14.2px; line-height: 19px; position: relative; box-shadow: 0 1px 0.5px rgba(11,20,26,.13); }
        .bubble-me { background: ${THEME.bgBubbleMe}; color: ${THEME.textMain}; border-top-right-radius: 0; }
        .bubble-other { background: ${THEME.bgBubbleOther}; color: ${THEME.textMain}; border-top-left-radius: 0; }
        .bubble-bot { background: #fff; border-left: 4px solid ${THEME.primary}; border-top-left-radius: 0; }
        
        .bubble-author { font-size: 12.5px; font-weight: 500; color: ${THEME.primaryDark}; margin-bottom: 4px; display: flex; align-items: center; gap: 4px; }
        .bubble-text { white-space: pre-wrap; word-wrap: break-word; }
        
        .bubble-meta { float: right; margin-left: 10px; margin-top: 4px; display: flex; align-items: center; gap: 3px; position: relative; top: 3px; }
        .bubble-time { font-size: 11px; color: #667781; }
        .bubble-check svg { width: 16px; color: #53bdeb; }

        /* IMAGENS */
        .bubble-image { padding: 4px; background: ${THEME.bgBubbleMe}; }
        .bubble-other.bubble-image { background: #fff; }
        .image-wrapper { border-radius: 6px; overflow: hidden; cursor: pointer; max-width: 330px; }
        .image-wrapper img { display: block; width: 100%; height: auto; max-height: 400px; object-fit: cover; }
        
        /* FOOTER INPUT */
        .wm-footer { background: ${THEME.bgApp}; padding: 10px 16px; display: flex; align-items: center; z-index: 10; min-height: 62px; }
        .input-wrapper { flex: 1; display: flex; align-items: flex-end; gap: 10px; }
        .chat-textarea { flex: 1; background: #fff; border: 1px solid #fff; border-radius: 8px; padding: 9px 12px; font-size: 15px; resize: none; outline: none; min-height: 20px; max-height: 100px; color: #111b21; font-family: inherit; line-height: 1.4; overflow-y: auto; }
        .chat-textarea:focus { border-color: #fff; }
        
        .btn-send { width: 40px; height: 40px; border: none; background: transparent; cursor: pointer; color: #54656f; display: flex; align-items: center; justify-content: center; }
        .btn-send.active { color: #54656f; }
        .btn-send.active svg { fill: #54656f; }

        /* EMPTY STATE */
        .wm-empty-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; border-bottom: 6px solid #25d366; background: #eff2f5; }
        .empty-content { text-align: center; max-width: 560px; color: #41525d; }
        .empty-img { margin-bottom: 40px; }
        .wm-empty-state h2 { font-weight: 300; font-size: 32px; margin-bottom: 20px; color: #41525d; }
        .wm-empty-state p { font-size: 14px; line-height: 20px; color: #667781; margin-bottom: 30px; }
        .encryption-notice { font-size: 12px; color: #8696a0; margin-top: 40px; }

        /* LIGHTBOX */
        .lightbox-overlay { position: fixed; inset: 0; background: rgba(11,20,26,0.9); z-index: 9999; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); animation: fadeIn 0.2s; }
        .lightbox-content img { max-width: 90vw; max-height: 85vh; border-radius: 4px; box-shadow: 0 4px 20px rgba(0,0,0,0.5); }
        .lightbox-close { position: absolute; top: 20px; right: 20px; background: rgba(255,255,255,0.1); border: none; color: #fff; width: 42px; height: 42px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .lightbox-close:hover { background: rgba(255,255,255,0.2); }

        @keyframes fadeIn { from {opacity: 0} to {opacity: 1} }
      `}</style>
    </div>
  );
}